import { FunctionComponent, useCallback, useEffect, useState } from "react";
import Icon from "@mdi/react";
import { debounce } from "lodash";
import { mdiArrowTopRight, mdiClose, mdiRefresh } from "@mdi/js";
import {
  ClosingStockModel,
  DeviceInventoryModel,
} from "../models/closing_stock_model";
import { WarehouseModel } from "../models/warehouse_model";
import { ItemModelV2 } from "../models/item_model";
import {
  ToastType,
  useToast,
} from "../../../context_providers/toast/toast_ctx";
import { fetchGet, toQueryString } from "../../../service/api_client";
import LoadingWidget from "../../../context_providers/modal/loader";
import Pagination from "../../../ui/new_components/common/pagination";
import ItemSearchField from "../items/components/ItemSearchField";
import WarehouseMultiSelect from "../warehouse/warehouse_multi_select";
import FilterDropDown from "../../../ui/new_components/common/filter_drop_down";
import { GetWareHouseListApi } from "../inventory_basket/components/warehouse_repo";
import ItemSearchFieldV2 from "../items/item_search_fieldv2";

interface ClosingStockListScreenProps {
  records: ClosingStockModel[];
  onClick: (id: string) => void;
  sort_by: string;
  sort_order: number;
  pages: number;
  options: []; // Replace with actual type
  handleSort: (data: { sort: string; sort_order: number }) => void;
  handlePageChange: (page: number) => void;
}

export const DeviceStatus = {
  stocked: "Stocked",
  "stocked out": "Stocked Out",
  "Returned and Stocked": "Returned and Stocked",
  Replaced: "Replaced",
  "In Transit": "In Transit",
};

interface StockListFilter {
  device_id?: string;
  order_id?: string;
  item_code?: string;
  org_namespace?: string;
  status?: string[];
  item_name?: string;
  warehouses?: string[];
  item_type?: string;
  category?: string;
  warehouse_type?: string;
  warehouse_sub_type?: string;
  sub_category?: string;
  search?: string;
  page: number;
  count: number;
  all?: any;
  item_id?: string;
}

const defaultFilter = (): StockListFilter => {
  return {
    page: 1,
    count: 100,
    item_name: "",
  };
};

const DeviceInventoryListScreen: FunctionComponent<
  ClosingStockListScreenProps
> = () => {
  const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/npd/device_inventory`;
  const { showToast } = useToast();
  const [count, setCount] = useState(50);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [data, setData] = useState<DeviceInventoryModel[]>([]);
  const tempFilter = localStorage.getItem("di_filter");
  const [filter, setFilter] = useState<StockListFilter>(
    tempFilter ? JSON.parse(tempFilter) : defaultFilter,
  );

  const [items, setItems] = useState<ItemModelV2>();
  const [selectedWarehouses, setSelectedWarehouses] = useState<
    WarehouseModel[]
  >([]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedHandler = useCallback(
    debounce((prop: StockListFilter) => {
      if (prop) fetchData(prop);
    }, 700),
    [],
  );
  useEffect(() => {
    localStorage.setItem("di_filter", JSON.stringify(filter));
  }, [filter]);

  useEffect(() => {
    debouncedHandler(filter);
  }, [filter, debouncedHandler]);

  useEffect(() => {
    const loadSelectedWarehouses = async () => {
      if (filter.warehouses && filter.warehouses.length > 0) {
        const res = await GetWareHouseListApi({
          page: 1,
          count: 1000,
          all: true,
        });
        if (res.success) {
          const allWarehouses = res.data.data;
          const hydrated = filter.warehouses
            .map((id) => allWarehouses.find((w: WarehouseModel) => w.id === id))
            .filter(Boolean) as WarehouseModel[];
          setSelectedWarehouses(hydrated);
        }
      }
    };

    loadSelectedWarehouses();
  }, []);

  const fetchData = async (prop: StockListFilter) => {
    setLoading(true);
    try {
      const res = await fetchGet(url + toQueryString(prop));

      if (res.success) {
        setCount(res.data.metadata?.count);
        setData(res.data.data);
        if (res.data.metadata) {
          setTotal(res.data.metadata.total);
        }
      } else {
        showToast({ type: ToastType.error, text: res.error });
      }
    } catch (error: any) {
      showToast({ type: ToastType.error, text: error.message });
    } finally {
      setLoading(false);
    }
  };

  const getDeviceInventoryExport = async () => {
    try {
      setLoading(true);

      const searchProp = { ...filter };
      searchProp.all = true;
      const response = await ExportDeviceIntentory(searchProp);

      if (response.success) {
        showToast({
          type: ToastType.success,
          text: response.message,
        });
      } else {
        showToast({
          type: ToastType.error,
          text: response.message,
        });
      }
    } catch (error: any) {
      console.error("Export initiation error:", error);

      showToast({ type: ToastType.error, text: error.error });
    } finally {
      setLoading(false);
    }
  };

  const ExportDeviceIntentory = async (props: StockListFilter) => {
    const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/npd/device-inventory-export`;

    const res = await fetchGet(url + toQueryString(props));
    return res;
  };

  const clearLocalStore = () => {
    // Remove from localStorage
    localStorage.removeItem("di_filter");

    // Reset filter state
    const defaultFilterObj = defaultFilter();
    setFilter(defaultFilterObj);

    // Trigger UI updates and fetch
    debouncedHandler(defaultFilterObj);
    fetchData(defaultFilterObj);
  };

  return (
    <>
      {/* <Maintainence></Maintainence> */}

      <div
        id="page_container"
        className="flex flex-col justify-between h-full p-1"
      >
        <section
          id="top_section"
          className="flex justify-between items-end my-1"
        >
          <h1 className="font-bold text-2xl text-gray-500 flex items-center gap-3">
            Device Inventory
            <button
              id="refresh_btn"
              title="Refersh"
              onClick={() => debouncedHandler({ ...filter })}
            >
              <Icon path={mdiRefresh} className="h-6 w-6"></Icon>
            </button>
            <button
              id="clear_filter_btn"
              className="flex gap-1 items-center cursor-pointer rounded p-1 bg-myPrimaryColor hover:scale-105 duration-200 text-sm text-white transform"
              onClick={() => clearLocalStore()}
            >
              Clear Filter
            </button>
          </h1>

          <div className="text-sm flex flex-wrap items-center gap-2 m-3">
            <div className="flex flex-wrap text-sm items-center gap-2 ">
              <button
                id="export_btn"
                onClick={() => getDeviceInventoryExport()}
                className="rounded-lg border px-2 py-1 hover:bg-gray-800 hover:text-white flex gap-2 items-center"
              >
                <Icon path={mdiArrowTopRight} size="15"></Icon> Export
              </button>
            </div>
          </div>
        </section>

        <section className="flex items-start justify-start m-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-5 gap-6 w-full">
            <div className="">
              <label htmlFor="device_id" className="text-md pt-1 font-semibold">
                Device ID
              </label>
              <ItemSearchField
                defaulValue={filter.device_id ?? ""}
                onClear={() => {
                  setFilter((prevData) => ({
                    ...prevData,
                    device_id: "", // Directly updating item_code
                  }));
                }}
                onSubmit={(value: string) =>
                  setFilter((prevData) => ({
                    ...prevData,
                    device_id: value, // Directly updating item_code
                  }))
                }
              />
            </div>
            <div className="relative">
              <label htmlFor="item_code" className="text-md pt-1 font-semibold">
                Item Code
              </label>
              <ItemSearchField
                defaulValue={filter.item_code ?? ""}
                onClear={() => {
                  setFilter((prevData) => ({
                    ...prevData,
                    item_code: "", // Directly updating item_code
                  }));
                }}
                onSubmit={(value: string) =>
                  setFilter((prevData) => ({
                    ...prevData,
                    item_code: value, // Directly updating item_code
                  }))
                }
              />
            </div>
            <div className="relative w-full">
              <label
                htmlFor="product_name"
                className="text-md pt-1 font-semibold"
              >
                Item Name
              </label>
              <span>
                <ItemSearchFieldV2
                  id="item_name_search"
                  placeholder="Select Item"
                  onSelect={(d) => {
                    setFilter((o) => ({
                      ...o,
                      item_id: d?.id ?? undefined,
                      item_name: d?.name ?? undefined,
                    }));
                    setItems(d);
                  }}
                  value={items}
                />
                {filter.item_id && (
                  <button
                    id="clear_item_btn"
                    title="Clear vendor"
                    onClick={() => {
                      setFilter((o) => ({
                        ...o,
                        item_id: undefined,
                        item_name: undefined,
                      }));
                      setItems(undefined);
                    }}
                    // className="flex gap-1 items-center cursor-pointer rounded p-1 border hover:scale-105 duration-200 transform bg-white"
                    className="absolute right-2 top-1/2 transform -translate-y-1/4 bg-white hover:scale-105 p-1 rounded"
                  >
                    <Icon path={mdiClose} size={0.7}></Icon>
                  </button>
                )}
              </span>
            </div>
            <div className="">
              <label className="text-md pt-1 font-semibold">Warehouse</label>
              <WarehouseMultiSelect
                onSelect={(selectedWarehouse: WarehouseModel) => {
                  setFilter((prevFilter) => {
                    // Prevent duplicate selections
                    if (
                      prevFilter.warehouses?.some(
                        (w) => w === selectedWarehouse.id,
                      )
                    ) {
                      return prevFilter; // Ignore duplicate entry
                    }

                    return {
                      ...prevFilter,
                      warehouses: [
                        ...(prevFilter?.warehouses || []),
                        selectedWarehouse.id,
                      ],
                    };
                  });

                  setSelectedWarehouses((prevWarehouses) => {
                    // Prevent duplicate selections
                    if (
                      prevWarehouses.some((v) => v.id === selectedWarehouse.id)
                    ) {
                      return prevWarehouses; // Ignore duplicate entry
                    }

                    return [...prevWarehouses, selectedWarehouse]; // Append the new vendor
                  });
                }}
                onDelete={(id: string) => {
                  setFilter((prevFilter) => ({
                    ...prevFilter,
                    warehouses: (prevFilter?.warehouses || []).filter(
                      (w) => w !== id,
                    ),
                  }));

                  setSelectedWarehouses((prevWarehouses) =>
                    prevWarehouses.filter((v) => v.id !== id),
                  );
                }}
                selectedWarehouses={selectedWarehouses ?? []}
              />
            </div>

            {/* <div>
              <label
                htmlFor="installation_type"
                className="text-md pt-1 font-semibold"
              >
                Warehouse Type
              </label>
              <select
                className="w-full p-2 bg-white rounded focus:outline-none"
                name="warehouse_type"
                id="warehouse_type"
                value={filter.warehouse_type ?? ""}
                onChange={(e) => {
                  setFilter((prevData) => ({
                    ...prevData,
                    warehouse_type: e.target.value,
                  }));
                }}
              >
                <option value="">select</option>
                <option value="internal">Internal</option>
                <option value="external">External</option>
              </select>
            </div> */}

            {/* <div>
              <label
                htmlFor="installation_type"
                className="text-md pt-1 font-semibold"
              >
                Warehouse Sub-Type
              </label>
              <select
                className="w-full p-2 bg-white rounded focus:outline-none"
                name="warehouse_sub_type"
                id="warehouse_sub_type"
                value={filter.warehouse_sub_type ?? ""}
                onChange={(e) => {
                  setFilter((prevData) => ({
                    ...prevData,
                    warehouse_sub_type: e.target.value,
                  }));
                }}
              >
                {filter.warehouse_type === "internal" && (
                  <>
                    <option value="">select</option>
                    <option value="kazam warehouse">
                      Internal Kazam Warehouse
                    </option>
                    <option value="city engineer warehouse">
                      City Engineer Warehouse
                    </option>
                  </>
                )}
                {filter.warehouse_type === "external" && (
                  <>
                    <option value="">select</option>
                    <option value="manpower">Manpower</option>
                    <option value="vendor">Vendor</option>
                  </>
                )}
              </select>
            </div> */}

            <div className="">
              <label htmlFor="order_id" className="text-md pt-1 font-semibold">
                Order ID
              </label>
              <ItemSearchField
                defaulValue={filter.order_id ?? ""}
                onClear={() => {
                  setFilter((prevData) => ({
                    ...prevData,
                    order_id: "", // Directly updating item name
                  }));
                }}
                onSubmit={(val) => {
                  setFilter((prevData) => ({
                    ...prevData,
                    order_id: val, // Directly updating item name
                  }));
                }}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-md">Device Status</label>
              <FilterDropDown
                placeholder="Device Status"
                onClearAll={() =>
                  setFilter({
                    ...filter,
                    status: undefined, // Fixed indentation
                  })
                }
                onDelete={(type) => {
                  setFilter({
                    ...filter,
                    status: filter?.status?.filter((f: any) => f !== type),
                  });
                }}
                onSelect={(type) => {
                  setFilter((old) => ({
                    ...old,
                    status: [...(old?.status ?? []), type],
                  }));
                }}
                selectedOptions={filter?.status ?? []}
                options={Object.values(DeviceStatus).map((t) => ({
                  label: t,
                  value: t,
                }))}
              />
            </div>
          </div>
        </section>

        <div className="my-2 border overflow-auto" style={{ height: "80%" }}>
          <table className="w-full     text-xs divide-y divide-gray-200 relative ">
            <thead className="  text-xs   py-2 sticky -top-1 bg-white z-10">
              <tr className="border ">
                <th className="px-6 py-3 whitespace-nowrap text-left">
                  Device ID
                </th>
                {/* <th className="px-6 py-3 whitespace-nowrap text-left">
                  Org namespace
                </th> */}
                <th className="px-6 py-3 whitespace-nowrap text-left">
                  Item Code
                </th>
                <th className="px-6 py-3 w-40 text-left">Item Name</th>
                {/* <th className="px-6 py-3 whitespace-nowrap text-left">
                  Packaging Code
                </th> */}
                <th className="px-6 py-3 whitespace-nowrap text-left">
                  Warehouse
                </th>
                <th className="px-6 py-3 whitespace-nowrap text-left">
                  Order Id
                </th>
                {/* <th className="px-6 py-3 whitespace-nowrap text-left">
                  Sale Type
                </th> */}
                <th className="px-6 py-3 whitespace-nowrap text-left">
                  Status
                </th>
                {/* <th className="px-6 py-3 whitespace-nowrap text-left">
                  Inward Date
                </th> */}
                {/* <th className="px-6 py-3 whitespace-nowrap text-left">
                  Outward Date
                </th> */}
                {/* <th className="px-6 py-3 whitespace-nowrap text-left">
                  Inventory Age
                </th> */}
              </tr>
            </thead>
            <tbody className="">
              {data?.length > 0 ? (
                data.map((data, i) => {
                  return (
                    <tr
                      id={`device_row_${data.unique_id ?? data.id ?? i}`}
                      className="border text-center "
                      key={i}
                    >
                      <td className="px-6 py-3 whitespace-nowrap text-left">
                        {data?.unique_id}
                      </td>

                      {/* <td className="px-6 py-3 whitespace-nowrap text-left">
                        {data?.org_namespace}
                      </td> */}

                      <td className="px-6 py-3 whitespace-nowrap text-left">
                        {data?.item_code}
                      </td>
                      <td className="px-6 py-3 w-40 truncate text-left">
                        {data?.item_name}
                      </td>
                      {/* <td className="px-6 py-3 whitespace-nowrap text-left">
                        {data?.packaging_code ?? ""}
                      </td> */}
                      <td className="px-6 py-3 whitespace-nowrap text-left">
                        {data?.warehouse}
                      </td>

                      <td className="px-6 py-3 whitespace-nowrap text-left">
                        {data?.order_id}
                      </td>
                      {/* <td className="px-6 py-3 whitespace-nowrap text-left">
                        {data?.sales_type ?? ""}
                      </td> */}
                      <td className="px-6 py-3 whitespace-nowrap text-left">
                        {data?.status ?? " "}
                      </td>
                      {/* <td className="px-6 py-3 whitespace-nowrap text-left">
                        {data?.inventory_age != null
                          ? `${data.inventory_age} days`
                          : ""}
                      </td> */}
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={13}>
                    <div className="h-56 text-center p-5 my-20">
                      <h5 className="text-gray-400 text-2xl">No Data found!</h5>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="mt-3">
          <Pagination
            itemCount={count}
            page={filter.page}
            total={total}
            count={filter.count}
            onChange={(page) => setFilter((o) => ({ ...o, page }))}
          />
        </div>
      </div>
      {loading && <LoadingWidget />}
    </>
  );
};

export default DeviceInventoryListScreen;
